<!-- A picture, as characters, in a box you can copy.
  --
  -- WHY THIS REPLACES THE RESULTS TABLE RATHER THAN JOINING IT. Every other
  -- tool here produces a FILE: a stage, a row per version, a byte count, a
  -- saving, a Download. This produces TEXT. There is nothing to put in a table,
  -- no second version to choose between, and no saving to report -- a 2.9 MB
  -- photograph coming back as 8 KB of text is not the same artefact made
  -- smaller, and printing "99.7% smaller" would be the `--11%` fault again:
  -- a figure whose meaning does not survive the thing it describes changing.
  --
  -- So the honest figures are the ones a person can act on: how many columns
  -- and rows they got, and how much text it is to paste.
  -->
<template>
    <div id="ascii-panel" class="mx-auto max-w-screen-xl px-4 md:px-8 w-full flex flex-col gap-5">

        <!-- TWO GROUPS, AND THE SPLIT IS NOT COSMETIC.
          -- 
          -- These four controls were one flex-wrap row, which put a slider, a
          -- select and two checkboxes at four different heights and read as a
          -- pile rather than a panel. Grouping them by WHAT THEY DECIDE is
          -- what makes them legible:
          --
          --   what the picture is    -- how wide, and which characters
          --   where it is going      -- the two settings that are ONLY about
          --                             the destination, and are the two most
          --                             easily got wrong
          --
          -- The dark-background and colour-codes choices genuinely belong
          -- together: neither changes the picture, both answer "where will you
          -- paste this?". A visitor who reads that heading has been told what
          -- to think about, which is the whole job of a group label. -->
        <div class="grid gap-5 md:grid-cols-2">

            <fieldset class="flex flex-col gap-4 rounded-2xl border border-rule bg-raised p-4">
                <legend class="px-1 text-xs font-bold uppercase tracking-[0.09em] text-ink-label">
                    {{ t('ascii.groupPicture') }}
                </legend>

                <!-- COLUMNS, NOT PIXELS, and the label has to say so. Every
                  -- other width control on this site is in pixels; this output
                  -- has none. A slider marked only "width" here would be read
                  -- as pixels and the number would look absurd. -->
                <label class="flex flex-col gap-1.5">
                    <span class="text-[15px] font-bold">{{ t('ascii.cols', { n: uiStore.asciiCols }) }}</span>
                    <input type="range" data-ascii-cols
                           :min="20" :max="300" step="10"
                           v-model.number="uiStore.asciiCols"
                           class="range range-sm range-primary"
                           @change="$emit('run')" />
                    <span class="text-[13px] text-ink-mute leading-snug">{{ t('ascii.colsNote') }}</span>
                </label>

                <!-- CONTRAST, and it is not one of jp2a's settings.
                  -- 
                  -- Reported against a photograph of the Taj Mahal that came
                  -- back as mush -- white marble on a pale sky, so the
                  -- building landed on two or three characters of the ramp.
                  -- This flattens the histogram before jp2a reads the picture.
                  --
                  -- A slider rather than something automatic because the
                  -- trade-off is per-image and measured: it rescues a flat
                  -- subject and it roughens a sky that was already smooth.
                  -- The numbers are in ascii_art.py. -->
                <label class="flex flex-col gap-1.5">
                    <span class="text-[15px] font-bold">{{ t('ascii.contrast', { n: uiStore.asciiContrast }) }}</span>
                    <input type="range" data-ascii-contrast
                           :min="0" :max="100" step="5"
                           v-model.number="uiStore.asciiContrast"
                           class="range range-sm range-primary"
                           @change="$emit('run')" />
                    <span class="text-[13px] text-ink-mute leading-snug">{{ t('ascii.contrastNote') }}</span>
                </label>

                <!-- EDGES, and this is jp2a's own --edge-threshold.
                  --
                  -- The one control here that is genuinely about SHAPE rather
                  -- than tone. Above the chosen gradient a cell is drawn with
                  -- a directional glyph (-/|\) instead of a ramp character, so
                  -- a hard boundary comes out as a line rather than a smear of
                  -- punctuation.
                  --
                  -- OFF by default, and a slider rather than a checkbox, for
                  -- the reason the measurements in ascii_art.py give: on line
                  -- art it redraws up to a third of the picture, on a
                  -- photograph it changes almost nothing at any setting, and
                  -- pushed too far it eats lettering -- every stroke of a
                  -- letter is an edge. That is a per-picture trade, which is
                  -- the same argument the contrast slider above makes.
                  --
                  -- In "The picture" rather than "Where you'll paste it": like
                  -- the contrast and the background removal, it changes what
                  -- the picture IS. -->
                <label class="flex flex-col gap-1.5">
                    <span class="text-[15px] font-bold">{{ t('ascii.edges', { n: uiStore.asciiEdges }) }}</span>
                    <input type="range" data-ascii-edges
                           :min="0" :max="100" step="5"
                           v-model.number="uiStore.asciiEdges"
                           class="range range-sm range-primary"
                           @change="$emit('run')" />
                    <span class="text-[13px] text-ink-mute leading-snug">{{ t('ascii.edgesNote') }}</span>
                </label>

                <!-- BEST-EFFORT, and the label says so because the failure
                  -- is common and destructive. ImageEngine's subject detection
                  -- is built for product photographs -- one thing on a plain
                  -- ground -- and on anything else it removes the wrong half.
                  -- Measured on a photograph of the Taj Mahal: it took the
                  -- building and the sky and left the fountain.
                  --
                  -- In "The picture" rather than "Where you'll paste it": it
                  -- changes what the picture IS, not where it is going.
                  --
                  -- It also does a different job from the contrast slider,
                  -- which is worth not conflating. Contrast spreads a flat
                  -- subject's tones. This one does not touch the subject's
                  -- tones at all -- measured, their spread FALLS from 5.10 to
                  -- 4.50 -- it empties the background, so a third of the cells
                  -- go blank and the eye finds the shape. Isolation, not
                  -- range. -->
                <label class="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" data-ascii-nobg
                           v-model="uiStore.asciiNoBg"
                           class="checkbox checkbox-sm checkbox-primary mt-[2px]"
                           @change="$emit('run')" />
                    <span class="flex flex-col gap-0.5">
                        <span class="text-[15px] font-bold">{{ t('ascii.nobg') }}</span>
                        <span class="text-[13px] text-ink-mute leading-snug">{{ t('ascii.nobgNote') }}</span>
                    </span>
                </label>

                <label class="flex flex-col gap-1.5">
                    <span class="text-[15px] font-bold">{{ t('ascii.ramp') }}</span>
                    <select data-ascii-ramp v-model="uiStore.asciiRamp"
                            class="select select-sm rounded-lg w-full max-w-xs"
                            @change="$emit('run')">
                        <option v-for="r in ramps" :key="r" :value="r">{{ t('ascii.ramps.' + r) }}</option>
                    </select>
                </label>
            </fieldset>

            <fieldset class="flex flex-col gap-4 rounded-2xl border border-rule bg-raised p-4">
                <legend class="px-1 text-xs font-bold uppercase tracking-[0.09em] text-ink-label">
                    {{ t('ascii.groupDestination') }}
                </legend>

                <label class="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" data-ascii-dark
                           v-model="uiStore.asciiDark"
                           class="checkbox checkbox-sm checkbox-primary mt-[2px]"
                           @change="$emit('run')" />
                    <span class="flex flex-col gap-0.5">
                        <span class="text-[15px] font-bold">{{ t('ascii.dark') }}</span>
                        <span class="text-[13px] text-ink-mute leading-snug">{{ t('ascii.darkNote') }}</span>
                    </span>
                </label>

                <!-- THE CHECKBOX SAYS WHAT IT COSTS. "Colour" on its own reads
                  -- as "make it prettier"; what it does is put terminal escape
                  -- codes in the text. Measured at 100 columns: 6.5 KB plain
                  -- against 140 KB with true colour, and pasted anywhere that
                  -- does not render ANSI -- a README, a chat window, an issue
                  -- -- those codes show as [38;2;140;98;71m litter. So the
                  -- label names the mechanism and the note names the cost, and
                  -- it points at the download that does colour properly. -->
                <label class="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" data-ascii-colours
                           v-model="uiStore.asciiColours"
                           class="checkbox checkbox-sm checkbox-primary mt-[2px]"
                           @change="$emit('run')" />
                    <span class="flex flex-col gap-0.5">
                        <span class="text-[15px] font-bold">{{ t('ascii.colour') }}</span>
                        <span class="text-[13px] text-ink-mute leading-snug">{{ t('ascii.colourNote') }}</span>
                    </span>
                </label>
            </fieldset>
        </div>

        <div v-if="uiStore.asciiError" id="ascii-error"
             class="text-[15px] text-ink-soft">{{ uiStore.asciiError }}</div>

        <div v-if="uiStore.asciiRunning" class="flex items-center gap-3">
            <svg class="h-5 w-5 animate-spin text-brand" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="4" class="opacity-20"></circle>
                <path d="M44 24a20 20 0 0 0-20-20" stroke="currentColor" stroke-width="4" stroke-linecap="round"></path>
            </svg>
            <span class="text-[15px] text-ink-soft">{{ t('ascii.working') }}</span>
        </div>

        <template v-if="uiStore.asciiArt">
            <div class="flex flex-wrap items-center justify-between gap-3">
                <!-- Columns and rows, and the size of the paste. Not pixels,
                  -- and not a saving. -->
                <span class="text-[15px] text-ink-soft" id="ascii-measure">
                    {{ t('ascii.measure', {cols: uiStore.asciiCols, rows: uiStore.asciiRows, size: prettyBytes}) }}
                </span>
                <div class="flex flex-wrap items-center gap-2">
                    <button type="button" data-ascii-copy
                            class="btn btn-primary btn-sm rounded-lg gap-2"
                            @click="copy">
                        <!-- Two sheets of paper, which is what a copy button
                          -- looks like everywhere. Swapped for a tick once it
                          -- has happened, so the icon and the words agree. -->
                        <svg v-if="!uiStore.asciiCopied" class="w-4 h-4" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" stroke-width="2" stroke-linecap="round"
                             stroke-linejoin="round" aria-hidden="true">
                            <rect x="9" y="9" width="11" height="11" rx="2" />
                            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                        </svg>
                        <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                             stroke-linejoin="round" aria-hidden="true">
                            <path d="m5 13 4 4L19 7" />
                        </svg>
                        {{ uiStore.asciiCopied ? t('ascii.copied') : t('ascii.copy') }}
                    </button>

                    <button type="button" data-ascii-txt
                            class="btn btn-outline btn-sm rounded-lg gap-2"
                            @click="downloadText">
                        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" />
                        </svg>
                        {{ t('ascii.downloadTxt') }}
                    </button>

                    <!-- HTML IS WHERE COLOUR ACTUALLY WORKS, and that is the
                      -- argument for having it at all. There are three ways
                      -- this can leave the page and only one keeps the colour
                      -- usefully: plain text pastes anywhere monospaced and
                      -- has no colour to lose; text with ANSI codes is
                      -- readable only in a terminal; HTML renders in any
                      -- browser and can be sent to somebody.
                      --
                      -- Built from the SAME segments the box is painted from,
                      -- not from jp2a's --htmlls. One parse means the file and
                      -- what you were looking at agree by construction rather
                      -- than because two code paths happen to match -- the
                      -- argument layoutCaption() makes for the meme renderer. -->
                    <button type="button" data-ascii-html
                            class="btn btn-outline btn-sm rounded-lg gap-2"
                            @click="downloadHtml">
                        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="m9 9-3 3 3 3" /><path d="m15 9 3 3-3 3" />
                            <rect x="3" y="4" width="18" height="16" rx="2" />
                        </svg>
                        {{ t('ascii.downloadHtml') }}
                    </button>

                    <!-- AND A PICTURE, which is the only form of this that
                      -- can be posted anywhere at all.
                      --
                      -- The other three all depend on the receiving end using
                      -- a monospace font and not reflowing: a README, a
                      -- terminal, a code block. Paste text art into a chat
                      -- message, a slide or anywhere social and it shears,
                      -- because a proportional font gives every character its
                      -- own advance -- measured in DejaVu Sans at 40px, the
                      -- classic ramp runs 11.0px to 39.5px across nine
                      -- distinct widths. A PNG has no such dependency.
                      --
                      -- Rendered from the SAME segments as the box and the
                      -- HTML file. Three renderings, one answer about where
                      -- the colours change. -->
                    <button type="button" data-ascii-png
                            class="btn btn-outline btn-sm rounded-lg gap-2"
                            @click="downloadPng">
                        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" />
                        </svg>
                        {{ t('ascii.downloadPng') }}
                    </button>
                </div>
            </div>

            <!-- A <pre>, and the whole point is that nothing reflows it.
              --
              -- Monospace is not enough on its own: the box has to scroll
              -- horizontally rather than wrap, because a wrapped line puts
              -- half a row of the picture underneath the other half and the
              -- image stops being an image. white-space: pre and overflow-x
              -- are doing the work; the font-size shrinks with the column
              -- count so a 300-column render still fits something readable.
              --
              -- THE COLOUR IS RENDERED, and the first version got this wrong.
              -- It printed jp2a's escape codes as literal text, on the
              -- reasoning that the box should show exactly what the clipboard
              -- gets. That argument is coherent and the result was unusable:
              -- with colour ticked the box filled with [38;2;233;233;245m and
              -- the picture was nowhere in it. Reported as "something isn't
              -- right", which is the correct reading -- it looks like a fault.
              --
              -- So the box paints the colour and the button still copies the
              -- raw text with the codes in. They differ deliberately now, and
              -- the checkbox's own note is what says the copied text carries
              -- codes.
              --
              -- The ground follows the dark checkbox: art rendered for a dark
              -- background is inverted, so showing it on white is showing a
              -- negative. This is the one place the two must agree. -->
            <!-- HOW TO ACTUALLY SEE IT IN A TERMINAL.
              --
              -- Collapsed, and in a <details> rather than behind a click
              -- handler: it is native, it needs no script, it is keyboard
              -- reachable, and it survives the service worker serving a stale
              -- stylesheet -- the argument the tools menu records for not
              -- leaning on CSS to decide whether something is open.
              --
              -- EVERY COMMAND IN HERE WAS RUN. `less` without -R refuses the
              -- file outright ("may be a binary file"), PowerShell's
              -- Get-Content -Raw passes the escapes through intact, and
              -- PowerShell 5.1 -- still the default on Windows -- does NOT
              -- understand the `e escape that every snippet on the internet
              -- uses, because that arrived in PowerShell 6. $e=[char]27 works
              -- on both. What is deliberately NOT claimed is which terminals
              -- render 24-bit colour: that cannot be measured from here, it
              -- ages badly, and the self-test below answers it better than a
              -- compatibility table would. -->
            <details id="ascii-help" class="rounded-2xl border border-rule bg-raised">
                <summary class="cursor-pointer select-none px-4 py-3 text-[15px] font-bold">
                    {{ t('ascii.help.title') }}
                </summary>
                <div class="flex flex-col gap-4 border-t border-rule px-4 py-4 text-[15px] text-ink-soft">

                    <p>{{ t('ascii.help.intro', {file: txtName}) }}</p>

                    <div class="grid gap-3 md:grid-cols-3">
                        <div v-for="os in shells" :key="os.key" class="flex flex-col gap-1.5">
                            <span class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">
                                {{ t('ascii.help.' + os.key) }}
                            </span>
                            <code class="rounded-lg bg-base-100 px-2.5 py-2 font-mono text-[13px] break-all">{{ os.cmd }}</code>
                            <span v-if="os.note" class="text-[13px] text-ink-mute leading-snug">{{ os.note }}</span>
                        </div>
                    </div>

                    <!-- WIDTH IS THE MOST LIKELY THING TO LOOK BROKEN, so it
                      -- is said before the colour advice. Every line is
                      -- exactly as many characters as the slider says, and one
                      -- wider than the window wraps into the row beneath it,
                      -- which shears the picture into nonsense. -->
                    <p>{{ t('ascii.help.width', {cols: uiStore.asciiCols}) }}</p>

                    <template v-if="uiStore.asciiColours">
                        <p>{{ t('ascii.help.colour') }}</p>
                        <div class="flex flex-col gap-1.5">
                            <span class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">
                                {{ t('ascii.help.testTitle') }}
                            </span>
                            <code class="rounded-lg bg-base-100 px-2.5 py-2 font-mono text-[13px] break-all">{{ colourTest }}</code>
                            <span class="text-[13px] text-ink-mute leading-snug">{{ t('ascii.help.testNote') }}</span>
                        </div>
                    </template>
                    <p v-else>{{ t('ascii.help.noColour') }}</p>
                </div>
            </details>

            <!-- NOT EVERY PICTURE SUITS THIS, said out loud.
              --
              -- Asked for after a photograph of the Taj Mahal came back as
              -- mush and the contrast slider only partly rescued it. It is
              -- the honest thing to put on the page: a hundred characters is
              -- about two thousand pixels' worth of information reduced to
              -- twenty-three levels of grey, and a busy or low-contrast
              -- photograph loses whatever made it legible. No setting fixes
              -- that, and a tool that implies one exists sends people round
              -- the sliders looking for it.
              --
              -- Placed under the figures rather than up with the controls,
              -- because that is where somebody looking at a disappointing
              -- result is already looking. -->
            <p id="ascii-suitability" class="text-[13px] text-ink-mute leading-snug max-w-2xl">
                {{ t('ascii.suitability') }}
            </p>

            <!-- CENTRED, which needs two elements rather than text-align.
              -- 
              -- A <pre> is a block filling its container, so aligning its text
              -- does nothing useful to pre-formatted lines. What centres it is
              -- the <pre> shrinking to its own content (w-fit) and being
              -- pushed to the middle by auto margins -- while the SCROLLING
              -- stays on the wrapper outside it. Put overflow on the <pre> and
              -- the auto margins have nothing to centre within; put it on the
              -- wrapper and a render wider than the page still scrolls,
              -- centred when it fits and flush-left when it does not, which is
              -- the right behaviour in both cases. -->
            <div class="rounded-2xl border border-rule overflow-x-auto p-4"
                 :class="uiStore.asciiDark ? 'bg-black' : 'bg-white'">
                <pre id="ascii-out" data-ascii-out
                     class="w-fit mx-auto leading-[1.05] font-mono"
                     :class="uiStore.asciiDark ? 'text-white' : 'text-black'"
                     :style="{fontSize: fontSize}"><span
                        v-for="(seg, i) in segments" :key="i"
                        :style="seg.colour ? {color: seg.colour} : null">{{ seg.text }}</span></pre>
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useUiElements } from "./../store/uiElements"
import { parseAnsi, outputName, asciiHtml, drawAsciiPng } from "./../lib/asciiText"

defineEmits(["run"])

const { t } = useI18n()
const uiStore = useUiElements()

/* The same names the server's table has, in the same order. Kept short and in
 * one place: the labels are translated, the keys are not.
 *
 * "detailed" and "numbers" are last because they are the two that were added
 * for a measured reason rather than for a look, and both are worth reading
 * the note about before picking -- 70 steps against classic's 23, and digits
 * because every digit is the same width in a proportional font where the
 * classic ramp's characters are not. The figures are in ascii_art.py. */
const ramps = ["classic", "simple", "blocks", "minimal", "detailed", "numbers"]

/* THE COMMANDS, and the notes are the measured parts.
 *
 * Linux and macOS get the same line because `cat` is `cat`; what differs
 * between them is which terminal app people run, and that is exactly the
 * thing not claimed here. Windows gets PowerShell rather than cmd's `type`
 * because Get-Content -Raw was verified to pass the escapes through and
 * `type` could not be tested from this machine.
 */
const shells = computed(() => [
    {key: "linux", cmd: `cat ${txtName.value}`, note: t("ascii.help.lessNote")},
    {key: "mac", cmd: `cat ${txtName.value}`, note: t("ascii.help.macNote")},
    {key: "windows", cmd: `Get-Content ${txtName.value} -Raw`, note: t("ascii.help.winNote")}
])

/* A SELF-TEST INSTEAD OF A COMPATIBILITY TABLE.
 *
 * Whether a terminal paints 24-bit colour cannot be established from here and
 * a list of which ones do would be out of date within a year. One line the
 * visitor can run answers it for the terminal actually in front of them: if
 * the word comes out orange, the art will have its colours.
 *
 * $e=[char]27 for PowerShell, NOT `e -- measured: `e produces a literal "e"
 * on PowerShell 5.1, which is still what Windows ships, and every snippet
 * online uses it.
 */
const colourTest = "printf '\\033[38;2;255;120;0mORANGE\\033[0m\\n'"


/* Shrink the type as the picture gets wider.
 *
 * A 300-column render at a comfortable reading size is about 2,400px across,
 * so it would scroll almost entirely off screen. These are the sizes at which
 * each column count fits a normal window without the box becoming a slot.
 */
const fontSize = computed(() => {
    const c = uiStore.asciiCols
    if (c <= 60) return "13px"
    if (c <= 100) return "10px"
    if (c <= 160) return "7px"
    if (c <= 220) return "5px"
    return "4px"
})

/* jp2a's colour, turned into something the box can paint.
 *
 * Deliberately NOT v-html. The characters come from jp2a and can perfectly
 * well include < > and &, so building markup out of them would mean escaping
 * by hand and getting it right for ever. A list of {text, colour} rendered
 * with v-for lets Vue escape the text, which it already does correctly.
 *
 * Only SGR is handled -- ESC [ ... m -- because that is all jp2a emits. The
 * sequences that matter are the true-colour foreground (38;2;R;G;B) and the
 * reset (0, or an empty parameter list). Anything else is dropped rather than
 * guessed at, so an unrecognised sequence loses its colour instead of
 * appearing as litter in the middle of the picture.
 */
/* Where the colours change, decided in lib/asciiText.js.
 *
 * The box and the HTML download are two renderings of one answer, so only one
 * of them may decide where the stripes are -- the argument layoutCaption()
 * makes for the meme's two renderers. */
const segments = computed(() => parseAnsi(uiStore.asciiArt))

const prettyBytes = computed(() => {
    const b = uiStore.asciiBytes
    return b < 1024 ? `${b} B` : `${(b / 1024).toFixed(1)} KB`
})

/* Hand a file over without ever going near the network.
 *
 * The art was made on the server, but it is TEXT and it is already here -- so
 * building the file in the browser is both simpler and the only version that
 * cannot disagree with what is on screen. Same mechanism downloadBatchZip()
 * uses for the zip: a blob, an object URL, a click, and revoke afterwards
 * rather than leaking the URL for the life of the page.
 */
function handOver(body, filename, mime) {
    // A Blob is passed straight through. Wrapping one in another Blob works --
    // it is a valid BlobPart -- but it COPIES several megabytes of PNG for
    // nothing, and the type of the outer wrapper would then be the one that
    // decides the download. Only the text callers need building.
    const blob = body instanceof Blob ? body : new Blob([body], {type: mime})
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.style.display = "none"
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    // Not immediately: Safari has been observed to abandon the download if the
    // URL is revoked in the same tick as the click.
    setTimeout(() => URL.revokeObjectURL(url), 10000)
}

const txtName = computed(() => outputName(stagedName(), "txt"))

function stagedName() {
    const staged = uiStore.files && uiStore.files[0]
    return (staged && staged.file && staged.file.name) || "picture"
}

function downloadText() {
    // The RAW art, escape codes and all. A .txt with ANSI in it is exactly
    // what you want for a login banner you are going to `cat`, and exactly
    // what you do not want in a README -- which is what the checkbox decides,
    // so this hands over whatever was asked for rather than second-guessing.
    handOver(uiStore.asciiArt, outputName(stagedName(), "txt"),
             "text/plain;charset=utf-8")
}

function downloadHtml() {
    const name = outputName(stagedName(), "html")
    handOver(asciiHtml({
        art: uiStore.asciiArt,
        dark: uiStore.asciiDark,
        fontSize: fontSize.value,
        title: name
    }), name, "text/html;charset=utf-8")
}

/* The picture, handed over as a blob rather than as a data: URL.
 *
 * toDataURL() would be shorter and is the wrong tool here: it base64s the
 * whole file into a string, which is a third larger again, and a 2,500 x
 * 4,000 render is several megabytes before the encoding. toBlob hands the
 * bytes straight to the same handOver() the other two downloads use, so the
 * revoke-after-10s rule that keeps Safari from abandoning a download applies
 * to all three rather than to two of them.
 *
 * ASYNC, and that is why there is no return value to check. toBlob's callback
 * may arrive on a later frame, so the failure cases have to be handled inside
 * it -- a null blob is what a canvas hands back when it could not encode, and
 * saying nothing at that point would be a button that silently does nothing.
 */
function downloadPng() {
    const canvas = drawAsciiPng({
        art: uiStore.asciiArt,
        dark: uiStore.asciiDark
    })
    if (!canvas) {
        // asciiPngLayout() returns null only when the render cannot be made to
        // fit inside the canvas caps even at the smallest legible type. That
        // is a picture nobody could read anyway, and saying so beats handing
        // over the blank canvas iOS would otherwise produce -- see the note on
        // MAX_AREA in CLAUDE.md.
        uiStore.asciiError = t("ascii.pngTooBig")
        return
    }
    canvas.toBlob((blob) => {
        if (!blob) {
            uiStore.asciiError = t("ascii.pngFailed")
            return
        }
        handOver(blob, outputName(stagedName(), "png"), "image/png")
    }, "image/png")
}

/* Copy, with a fallback that is not a lie.
 *
 * navigator.clipboard needs a secure context, so on plain http -- which is
 * exactly how this is developed and tested -- it is undefined. Falling back to
 * selecting the text means the button still does something honest: the words
 * change to say it was copied only when it actually was.
 */
async function copy() {
    const text = uiStore.asciiArt
    let done = false
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text)
            done = true
        }
    } catch (err) { /* fall through to selecting it */ }

    if (!done) {
        const box = document.getElementById("ascii-out")
        if (box) {
            const range = document.createRange()
            range.selectNodeContents(box)
            const sel = window.getSelection()
            sel.removeAllRanges()
            sel.addRange(range)
            try { done = document.execCommand("copy") } catch (err) { done = false }
        }
    }

    uiStore.asciiCopied = done
    if (done) {
        setTimeout(() => { uiStore.asciiCopied = false }, 2000)
    }
}
</script>
